import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Avatar,
  AvatarGroup,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Description,
  Add,
  Close,
  ContentCopy,
  Article,
  CheckCircle,
  Edit,
  DeleteOutlined as DeleteOutline,
  Refresh,
  PersonAdd,
  Share,
  LoginOutlined
} from '@mui/icons-material';
import { formatNameFromEmail } from '../services/meetingService';
import { useAuth } from '../context/AuthContext';
import { BrdTemplate } from '../types';
import {
  listBrdTemplates,
  createBrdTemplate,
  updateBrdTemplate,
  deleteBrdTemplate,
  shareBrdTemplate,
  isTemplateOwner,
  formatTemplateDate,
  BrdApiError
} from '../services/brdTemplateService';

const SHARED_STORAGE_KEY = 'scrumai_brd_shared_users';

const getStoredSharedUsers = (): Record<string, string[]> => {
  try {
    const raw = localStorage.getItem(SHARED_STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch {
    // fallback
  }
  return {};
};

const setStoredSharedUsers = (data: Record<string, string[]>) => {
  try {
    localStorage.setItem(SHARED_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
};

const TemplatesPage: React.FC = () => {
  const { user, firebaseUser, openSignInModal } = useAuth();

  // Template collection state
  const [templates, setTemplates] = useState<BrdTemplate[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Fallback cache of shared collaborators per brd_id: { [brd_id]: string[] }
  const [sharedUsersMap, setSharedUsersMap] = useState<Record<string, string[]>>(getStoredSharedUsers);

  // Create Template Dialog State
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newReadme, setNewReadme] = useState('');
  const [newShareEmail, setNewShareEmail] = useState('');
  const [newSharedEmailsList, setNewSharedEmailsList] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit Template Dialog State
  const [editingTemplate, setEditingTemplate] = useState<BrdTemplate | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editReadme, setEditReadme] = useState('');
  const [editShareEmail, setEditShareEmail] = useState('');
  const [editSharedEmailsList, setEditSharedEmailsList] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Share Existing Template Dialog State
  const [sharingTemplate, setSharingTemplate] = useState<BrdTemplate | null>(null);
  const [quickShareEmail, setQuickShareEmail] = useState('');
  const [isQuickSharing, setIsQuickSharing] = useState(false);

  // Delete Template Dialog State
  const [deletingTemplate, setDeletingTemplate] = useState<BrdTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // View Template Dialog State
  const [viewingTemplate, setViewingTemplate] = useState<BrdTemplate | null>(null);
  const [copied, setCopied] = useState(false);

  // Toast Notification
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  /**
   * Loads templates from the Cloud Run REST API.
   */
  const loadTemplates = useCallback(
    async (showSuccessToast = false) => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const fetched = await listBrdTemplates();
        setTemplates(fetched);
        if (showSuccessToast) {
          setToastMessage(`Synced ${fetched.length} BRD template(s) from Cloud Run`);
        }
      } catch (err: any) {
        console.warn('Failed to load BRD templates:', err);
        const errorMsg =
          err instanceof BrdApiError
            ? err.message
            : 'Unable to load BRD templates. Please check your connection and try again.';
        setLoadError(errorMsg);

        if (err instanceof BrdApiError && err.status === 401) {
          openSignInModal('workspace');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [openSignInModal]
  );

  // Fetch templates when user or auth state changes
  useEffect(() => {
    if (user || firebaseUser) {
      loadTemplates(false);
    } else {
      setTemplates([]);
      setIsLoading(false);
    }
  }, [loadTemplates, user, firebaseUser]);

  /**
   * Handles opening Create modal (requires authentication).
   */
  const handleOpenCreate = () => {
    if (!user && !firebaseUser) {
      openSignInModal('workspace');
      return;
    }
    setNewTitle('');
    setNewReadme(
      `# BRD Template\n\n## 1. Executive Summary\nBriefly state the business goals, value proposition, and success criteria.\n\n## 2. In-Scope Requirements\n- Feature 1\n- Feature 2\n\n## 3. Out-of-Scope\n- Deferred enhancements\n\n## 4. User Personas & Problem Statement\n- Persona: Product Managers & Tech Leads\n- Problem Statement:\n\n## 5. Acceptance Criteria\n- [ ] Latency requirement\n- [ ] Zero ungrounded requirements`
    );
    setNewShareEmail('');
    setNewSharedEmailsList([]);
    setCreateError(null);
    setCreateOpen(true);
  };

  const handleAddEmailToNew = () => {
    const email = newShareEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (!newSharedEmailsList.includes(email)) {
      setNewSharedEmailsList([...newSharedEmailsList, email]);
      setNewShareEmail('');
    }
  };

  const handleRemoveEmailFromNew = (emailToRemove: string) => {
    setNewSharedEmailsList(newSharedEmailsList.filter((e) => e !== emailToRemove));
  };

  /**
   * Handles creating a new template via POST /brd-templates.
   */
  const handleCreateSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isCreating) return;

    const trimmedTitle = newTitle.trim();
    if (!trimmedTitle) {
      setCreateError('Please provide a name for the template.');
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      const response = await createBrdTemplate({
        brd_template_name: trimmedTitle,
        brd_template_readme: newReadme.trim()
      });

      // If participants were added, share each via POST /brd-templates/share
      const createdId = (response as any)?.brd_id;
      if (createdId && newSharedEmailsList.length > 0) {
        for (const email of newSharedEmailsList) {
          try {
            await shareBrdTemplate({ brd_id: createdId, user_email: email });
          } catch (e) {
            console.warn(`Could not share template with ${email}:`, e);
          }
        }
        const updatedMap = {
          ...sharedUsersMap,
          [createdId]: newSharedEmailsList
        };
        setSharedUsersMap(updatedMap);
        setStoredSharedUsers(updatedMap);
      }

      setToastMessage(`Template "${trimmedTitle}" created successfully!`);
      setCreateOpen(false);
      setNewTitle('');
      setNewReadme('');
      setNewSharedEmailsList([]);
      await loadTemplates(false);
    } catch (err: any) {
      console.error('Failed to create BRD template:', err);
      const msg =
        err instanceof BrdApiError
          ? err.message
          : 'Failed to create template. Please verify your inputs and try again.';
      setCreateError(msg);

      if (err instanceof BrdApiError && err.status === 401) {
        openSignInModal('workspace');
      }
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Opens Edit modal for an owned template.
   */
  const handleOpenEdit = (template: BrdTemplate) => {
    if (!isTemplateOwner(template, user?.email)) {
      setToastMessage('Only the template owner can edit this template.');
      return;
    }
    const currentShared = template.shared_users || sharedUsersMap[template.brd_id] || [];
    setEditingTemplate(template);
    setEditTitle(template.brd_template_name || '');
    setEditReadme(template.brd_template_readme || '');
    setEditShareEmail('');
    setEditSharedEmailsList(currentShared);
    setEditError(null);
  };

  const handleAddEmailToEdit = () => {
    const email = editShareEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (!editSharedEmailsList.includes(email)) {
      setEditSharedEmailsList([...editSharedEmailsList, email]);
      setEditShareEmail('');
    }
  };

  const handleRemoveEmailFromEdit = (emailToRemove: string) => {
    setEditSharedEmailsList(editSharedEmailsList.filter((e) => e !== emailToRemove));
  };

  /**
   * Handles updating a template via PUT /brd-templates/{brd_id}.
   */
  const handleEditSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isUpdating || !editingTemplate) return;

    const trimmedTitle = editTitle.trim();
    if (!trimmedTitle) {
      setEditError('Please provide a name for the template.');
      return;
    }

    setIsUpdating(true);
    setEditError(null);

    try {
      await updateBrdTemplate(editingTemplate.brd_id, {
        brd_template_name: trimmedTitle,
        brd_template_readme: editReadme.trim()
      });

      // Share with any newly added collaborators via POST /brd-templates/share
      const prevShared = editingTemplate.shared_users || sharedUsersMap[editingTemplate.brd_id] || [];
      const newlyAdded = editSharedEmailsList.filter((e) => !prevShared.includes(e));
      for (const email of newlyAdded) {
        try {
          await shareBrdTemplate({ brd_id: editingTemplate.brd_id, user_email: email });
        } catch (e) {
          console.warn(`Could not share template with ${email}:`, e);
        }
      }

      const updatedMap = {
        ...sharedUsersMap,
        [editingTemplate.brd_id]: editSharedEmailsList
      };
      setSharedUsersMap(updatedMap);
      setStoredSharedUsers(updatedMap);

      setToastMessage(`Template "${trimmedTitle}" updated successfully!`);

      // Refresh viewed template if currently open
      if (viewingTemplate?.brd_id === editingTemplate.brd_id) {
        setViewingTemplate((prev) =>
          prev
            ? {
                ...prev,
                brd_template_name: trimmedTitle,
                brd_template_readme: editReadme.trim(),
                shared_users: editSharedEmailsList
              }
            : null
        );
      }

      setEditingTemplate(null);
      await loadTemplates(false);
    } catch (err: any) {
      console.error('Failed to update BRD template:', err);
      const msg =
        err instanceof BrdApiError
          ? err.message
          : 'Failed to update template. Please try again.';
      setEditError(msg);

      if (err instanceof BrdApiError && err.status === 401) {
        openSignInModal('workspace');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Quick Share Existing Template Handler.
   * Only the template owner can share.
   */
  const handleOpenShare = (template: BrdTemplate) => {
    if (!isTemplateOwner(template, user?.email)) {
      setToastMessage('Only the template owner can share this template.');
      return;
    }
    setSharingTemplate(template);
    setQuickShareEmail('');
  };

  const handleShareExistingSubmit = async () => {
    if (!sharingTemplate || !isTemplateOwner(sharingTemplate, user?.email)) {
      setToastMessage('Only the template owner can share this template.');
      return;
    }
    const email = quickShareEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;

    setIsQuickSharing(true);
    try {
      await shareBrdTemplate({
        brd_id: sharingTemplate.brd_id,
        user_email: email
      });

      const currentList = sharingTemplate.shared_users || sharedUsersMap[sharingTemplate.brd_id] || [];
      const updatedList = currentList.includes(email) ? currentList : [...currentList, email];

      // Update sharing dialog template state
      setSharingTemplate({
        ...sharingTemplate,
        shared_users: updatedList
      });

      // Update template in the list state
      setTemplates((prev) =>
        prev.map((t) => (t.brd_id === sharingTemplate.brd_id ? { ...t, shared_users: updatedList } : t))
      );

      // Update viewing template if open
      if (viewingTemplate?.brd_id === sharingTemplate.brd_id) {
        setViewingTemplate((prev) => (prev ? { ...prev, shared_users: updatedList } : null));
      }

      const updatedMap = {
        ...sharedUsersMap,
        [sharingTemplate.brd_id]: updatedList
      };
      setSharedUsersMap(updatedMap);
      setStoredSharedUsers(updatedMap);

      setToastMessage(`Template "${sharingTemplate.brd_template_name}" shared with ${email}!`);
      setQuickShareEmail('');
      await loadTemplates(false);
    } catch (err: any) {
      console.error('Failed to share template via API:', err);
      const msg =
        err instanceof BrdApiError
          ? err.message
          : 'Failed to share template with teammate.';
      setToastMessage(msg);

      if (err instanceof BrdApiError && err.status === 401) {
        openSignInModal('workspace');
      }
    } finally {
      setIsQuickSharing(false);
    }
  };

  /**
   * Opens Delete confirmation modal.
   */
  const handleOpenDelete = (template: BrdTemplate) => {
    if (!isTemplateOwner(template, user?.email)) {
      setToastMessage('Only the template owner can delete this template.');
      return;
    }
    setDeletingTemplate(template);
    setDeleteError(null);
  };

  /**
   * Handles deleting a template via DELETE /brd-templates/{brd_id}.
   */
  const handleConfirmDelete = async () => {
    if (isDeleting || !deletingTemplate) return;

    setIsDeleting(true);
    setDeleteError(null);
    const targetTitle = deletingTemplate.brd_template_name;
    const targetId = deletingTemplate.brd_id;

    try {
      await deleteBrdTemplate(targetId);

      // Remove immediately from UI state
      setTemplates((prev) => prev.filter((t) => t.brd_id !== targetId));

      if (viewingTemplate?.brd_id === targetId) {
        setViewingTemplate(null);
      }

      setToastMessage(`Template "${targetTitle}" deleted successfully!`);
      setDeletingTemplate(null);
    } catch (err: any) {
      console.error('Failed to delete BRD template:', err);
      const msg =
        err instanceof BrdApiError
          ? err.message
          : 'Failed to delete template. Please try again.';
      setDeleteError(msg);

      if (err instanceof BrdApiError && err.status === 401) {
        openSignInModal('workspace');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Copies README markdown content to the clipboard.
   */
  const handleCopyReadme = () => {
    if (!viewingTemplate) return;
    navigator.clipboard.writeText(viewingTemplate.brd_template_readme || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isUserAuthenticated = Boolean(user || firebaseUser);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, height: '100%', overflowY: 'auto', bgcolor: 'var(--hp-bg)' }}>
      {/* Top Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--hp-text)' }}>
            BRD & Document Templates
          </Typography>
          <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ mt: 0.5 }}>
            Create, manage, and share standard Business Requirements Document (BRD) templates with teammates
          </Typography>
        </Box>

        {/* Action Controls */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => loadTemplates(true)}
            disabled={isLoading || !isUserAuthenticated}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Refresh />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              px: 2
            }}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            onClick={handleOpenCreate}
            startIcon={<Add />}
            sx={{
              bgcolor: 'var(--hp-primary)',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.85rem',
              px: 2.2,
              boxShadow: '0 4px 12px rgba(79, 107, 255, 0.25)',
              '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
            }}
          >
            Create New Template
          </Button>
        </Box>
      </Box>

      {/* Unauthenticated Notification Banner */}
      {!isUserAuthenticated && (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => openSignInModal('workspace')}
              startIcon={<LoginOutlined />}
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              Sign In
            </Button>
          }
          sx={{ mb: 3, borderRadius: '12px' }}
        >
          Sign in with your Google account to access, create, and share your cloud BRD templates.
        </Alert>
      )}

      {/* Error State Banner */}
      {loadError && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => loadTemplates(true)}
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              Retry
            </Button>
          }
          sx={{ mb: 3, borderRadius: '12px' }}
        >
          {loadError}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            gap: 2
          }}
        >
          <CircularProgress size={42} sx={{ color: 'var(--hp-primary)' }} />
          <Typography variant="body2" color="var(--hp-text-secondary)">
            Loading BRD templates from Cloud Run API...
          </Typography>
        </Box>
      ) : isUserAuthenticated && templates.length === 0 && !loadError ? (
        /* Empty State */
        <Box
          className="neumorphic-card"
          sx={{
            p: { xs: 4, sm: 6 },
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            my: 4
          }}
        >
          <Avatar
            sx={{
              bgcolor: 'var(--hp-primary-light)',
              color: 'var(--hp-primary)',
              width: 58,
              height: 58,
              mb: 1
            }}
          >
            <Description sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--hp-text)' }}>
            No BRD Templates Found
          </Typography>
          <Typography
            variant="body2"
            color="var(--hp-text-secondary)"
            sx={{ maxWidth: 480, lineHeight: 1.6 }}
          >
            You do not have any BRD templates yet. Create your first template or ask a teammate to share one with you.
          </Typography>
          <Button
            variant="contained"
            onClick={handleOpenCreate}
            startIcon={<Add />}
            sx={{
              mt: 1,
              bgcolor: 'var(--hp-primary)',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
            }}
          >
            Create Your First Template
          </Button>
        </Box>
      ) : (
        /* Templates Grid */
        <Grid container spacing={2.5}>
          {templates.map((template) => {
            const isOwner = isTemplateOwner(template, user?.email);
            const ownerDisplayName = formatNameFromEmail(template.brd_owner_email);
            const dateFormatted = formatTemplateDate(template.created_date);
            const sharedWithList = template.shared_users || sharedUsersMap[template.brd_id] || [];

            return (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={template.brd_id}>
                <Box
                  className="neumorphic-card"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.8,
                    p: 2.2
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
                      <Avatar
                        sx={{
                          bgcolor: 'var(--hp-primary-light)',
                          color: 'var(--hp-primary)',
                          width: 38,
                          height: 38
                        }}
                      >
                        <Article fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.96rem',
                            color: 'var(--hp-text)',
                            wordBreak: 'break-word'
                          }}
                        >
                          {template.brd_template_name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center', mt: 0.3, flexWrap: 'wrap' }}>
                          <Chip
                            size="small"
                            label="BRD"
                            sx={{
                              height: 20,
                              fontSize: '0.68rem',
                              bgcolor: 'var(--hp-surface)',
                              fontWeight: 600
                            }}
                          />
                          <Chip
                            size="small"
                            label={isOwner ? 'Owner' : 'Shared with you'}
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor: isOwner
                                ? 'rgba(34, 201, 154, 0.14)'
                                : 'rgba(79, 107, 255, 0.14)',
                              color: isOwner ? '#1ca87f' : 'var(--hp-primary)'
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Actions: Edit, Delete, Share - ONLY for template owner */}
                    {isOwner && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Edit template">
                          <IconButton
                            size="small"
                            aria-label={`Edit ${template.brd_template_name}`}
                            onClick={() => handleOpenEdit(template)}
                            sx={{
                              color: 'var(--hp-text)',
                              bgcolor: 'var(--hp-surface)',
                              border: '1px solid var(--hp-border)',
                              '&:hover': {
                                color: 'var(--hp-primary)',
                                bgcolor: 'var(--hp-primary-light)'
                              }
                            }}
                          >
                            <Edit sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete template">
                          <IconButton
                            size="small"
                            aria-label={`Delete ${template.brd_template_name}`}
                            onClick={() => handleOpenDelete(template)}
                            sx={{
                              color: '#FF6577',
                              bgcolor: 'var(--hp-surface)',
                              border: '1px solid var(--hp-border)',
                              '&:hover': { bgcolor: 'rgba(255, 101, 119, 0.1)' }
                            }}
                          >
                            <DeleteOutline sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        {/* Share Template Option on Card (Owner Only) */}
                        <Tooltip title="Share template with teammates">
                          <IconButton
                            size="small"
                            aria-label={`Share ${template.brd_template_name}`}
                            onClick={() => handleOpenShare(template)}
                            sx={{
                              color: 'var(--hp-primary)',
                              bgcolor: 'var(--hp-surface)',
                              border: '1px solid var(--hp-border)',
                              '&:hover': { bgcolor: 'rgba(79, 107, 255, 0.12)' }
                            }}
                          >
                            <Share sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>

                  {/* README Preview Excerpt */}
                  <Box
                    sx={{
                      bgcolor: 'var(--hp-bg)',
                      borderRadius: '10px',
                      p: 1.4,
                      fontSize: '0.78rem',
                      color: 'var(--hp-text-secondary)',
                      fontFamily: 'monospace',
                      lineHeight: 1.5,
                      maxHeight: 110,
                      overflow: 'hidden',
                      position: 'relative',
                      border: '1px solid var(--hp-border)'
                    }}
                  >
                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'inherit'
                      }}
                    >
                      {template.brd_template_readme || 'No description provided.'}
                    </pre>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 28,
                        background: 'linear-gradient(transparent, var(--hp-bg))'
                      }}
                    />
                  </Box>

                  {/* Shared Teammates Avatars Display */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {sharedWithList.length > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <AvatarGroup
                          max={4}
                          sx={{
                            '& .MuiAvatar-root': {
                              width: 24,
                              height: 24,
                              fontSize: '0.68rem',
                              bgcolor: 'var(--hp-primary)',
                              border: '2px solid var(--hp-surface)'
                            }
                          }}
                        >
                          {sharedWithList.map((email, idx) => (
                            <Tooltip key={idx} title={`Shared with ${email}`}>
                              <Avatar
                                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(formatNameFromEmail(email))}&background=4F6BFF&color=fff`}
                              >
                                {email.charAt(0).toUpperCase()}
                              </Avatar>
                            </Tooltip>
                          ))}
                        </AvatarGroup>
                        <Typography
                          variant="caption"
                          color="var(--hp-text-secondary)"
                          sx={{ fontSize: '0.74rem', fontWeight: 500 }}
                        >
                          {sharedWithList.length} shared
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        variant="caption"
                        color="var(--hp-text-secondary)"
                        sx={{ fontSize: '0.74rem' }}
                      >
                        Private (only you)
                      </Typography>
                    )}
                  </Box>

                  {/* Card Footer: Owner Info & View README Button */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: 'auto',
                      pt: 1.2,
                      borderTop: '1px solid var(--hp-border)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={template.brd_owner_email || 'Owner'}>
                        <Avatar
                          sx={{
                            width: 26,
                            height: 26,
                            fontSize: '0.72rem',
                            bgcolor: 'var(--hp-primary)',
                            color: '#fff'
                          }}
                        >
                          {(template.brd_owner_email || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                      </Tooltip>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontWeight: 600,
                            color: 'var(--hp-text)',
                            fontSize: '0.74rem',
                            lineHeight: 1.2
                          }}
                        >
                          {ownerDisplayName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="var(--hp-text-secondary)"
                          sx={{ fontSize: '0.68rem' }}
                        >
                          {dateFormatted}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Card Action: Only View README (NO USE button) */}
                    <Box sx={{ display: 'flex', gap: 0.8 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setViewingTemplate(template)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '8px',
                          fontSize: '0.74rem',
                          fontWeight: 600
                        }}
                      >
                        View README
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ======================================================== */}
      {/* CREATE NEW TEMPLATE MODAL DIALOG */}
      {/* ======================================================== */}
      <Dialog
        open={createOpen}
        onClose={() => !isCreating && setCreateOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: '20px', p: 1, bgcolor: 'var(--hp-surface)' }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description sx={{ color: 'var(--hp-primary)' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Create New BRD Template
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => !isCreating && setCreateOpen(false)} disabled={isCreating}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleCreateSubmit}>
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 2 }}>
            <Typography variant="body2" color="var(--hp-text-secondary)">
              Define a reusable BRD markdown template saved to the Cloud Run BigQuery backend.
            </Typography>

            {/* Template Name */}
            <TextField
              autoFocus
              label="Template Name"
              placeholder="e.g. Sprint Planning BRD"
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
                setCreateError(null);
              }}
              fullWidth
              size="small"
              required
              disabled={isCreating}
            />

            {/* README Content Input */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.86rem' }}>
                  BRD Template Content / README (Markdown)
                </Typography>
                <Typography variant="caption" color="var(--hp-text-secondary)">
                  Supports headers, bullet lists, tables, and acceptance criteria
                </Typography>
              </Box>

              <TextField
                multiline
                rows={10}
                placeholder={`# BRD Template\n\n## 1. Overview\nDescribe the requirements...\n\n## 2. Acceptance Criteria\n- [ ] Task 1`}
                value={newReadme}
                onChange={(e) => {
                  setNewReadme(e.target.value);
                  setCreateError(null);
                }}
                fullWidth
                disabled={isCreating}
                sx={{
                  fontFamily: 'monospace',
                  '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' }
                }}
              />
            </Box>

            {/* Share Template With Teammates Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.86rem' }}>
                Share Template with Teammates (Optional)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  placeholder="Enter teammate email (e.g. colleague@company.com)"
                  value={newShareEmail}
                  onChange={(e) => setNewShareEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEmailToNew();
                    }
                  }}
                  fullWidth
                  size="small"
                  disabled={isCreating}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddEmailToNew}
                  startIcon={<PersonAdd sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: 'none', borderRadius: '8px', px: 2, flexShrink: 0 }}
                  disabled={isCreating || !newShareEmail.trim()}
                >
                  Add User
                </Button>
              </Box>

              {newSharedEmailsList.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.5 }}>
                  {newSharedEmailsList.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveEmailFromNew(email)}
                      size="small"
                      sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', fontWeight: 600 }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {createError && (
              <Alert severity="error" sx={{ borderRadius: '8px', fontSize: '0.82rem' }}>
                {createError}
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setCreateOpen(false)}
              disabled={isCreating}
              sx={{ textTransform: 'none', color: 'var(--hp-text-secondary)' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isCreating || !newTitle.trim()}
              startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
              sx={{
                bgcolor: 'var(--hp-primary)',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                px: 2.5,
                '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
              }}
            >
              {isCreating ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ======================================================== */}
      {/* EDIT TEMPLATE MODAL DIALOG */}
      {/* ======================================================== */}
      <Dialog
        open={Boolean(editingTemplate)}
        onClose={() => !isUpdating && setEditingTemplate(null)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: '20px', p: 1, bgcolor: 'var(--hp-surface)' }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit sx={{ color: 'var(--hp-primary)' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Edit BRD Template
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => !isUpdating && setEditingTemplate(null)} disabled={isUpdating}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleEditSubmit}>
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 2 }}>
            <Typography variant="body2" color="var(--hp-text-secondary)">
              Update the template title, README specifications, and teammate collaboration.
            </Typography>

            {/* Template Name */}
            <TextField
              autoFocus
              label="Template Name"
              value={editTitle}
              onChange={(e) => {
                setEditTitle(e.target.value);
                setEditError(null);
              }}
              fullWidth
              size="small"
              required
              disabled={isUpdating}
            />

            {/* README Content */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.86rem' }}>
                  BRD Template Content / README (Markdown)
                </Typography>
                <Typography variant="caption" color="var(--hp-text-secondary)">
                  Supports headers, bullet lists, tables, and acceptance criteria
                </Typography>
              </Box>

              <TextField
                multiline
                rows={10}
                value={editReadme}
                onChange={(e) => {
                  setEditReadme(e.target.value);
                  setEditError(null);
                }}
                fullWidth
                disabled={isUpdating}
                sx={{
                  fontFamily: 'monospace',
                  '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' }
                }}
              />
            </Box>

            {/* Share Template With Teammates Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.86rem' }}>
                Shared Teammates (Collaborators)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  placeholder="Enter teammate email (e.g. colleague@company.com)"
                  value={editShareEmail}
                  onChange={(e) => setEditShareEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEmailToEdit();
                    }
                  }}
                  fullWidth
                  size="small"
                  disabled={isUpdating}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddEmailToEdit}
                  startIcon={<PersonAdd sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: 'none', borderRadius: '8px', px: 2, flexShrink: 0 }}
                  disabled={isUpdating || !editShareEmail.trim()}
                >
                  Add User
                </Button>
              </Box>

              {editSharedEmailsList.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.5 }}>
                  {editSharedEmailsList.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveEmailFromEdit(email)}
                      size="small"
                      sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', fontWeight: 600 }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {editError && (
              <Alert severity="error" sx={{ borderRadius: '8px', fontSize: '0.82rem' }}>
                {editError}
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setEditingTemplate(null)}
              disabled={isUpdating}
              sx={{ textTransform: 'none', color: 'var(--hp-text-secondary)' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isUpdating || !editTitle.trim()}
              startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
              sx={{
                bgcolor: 'var(--hp-primary)',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                px: 2.5,
                '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
              }}
            >
              {isUpdating ? 'Saving Changes...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ======================================================== */}
      {/* SHARE EXISTING TEMPLATE MODAL DIALOG */}
      {/* ======================================================== */}
      <Dialog
        open={Boolean(sharingTemplate)}
        onClose={() => setSharingTemplate(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: '18px', p: 1, bgcolor: 'var(--hp-surface)' } }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Share sx={{ color: 'var(--hp-primary)' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
              Share BRD Template
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setSharingTemplate(null)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1.5 }}>
          <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ fontSize: '0.84rem' }}>
            Share <strong>{sharingTemplate?.brd_template_name}</strong> with teammates by entering their email address.
          </Typography>

          {/* Email input */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              autoFocus
              label="Teammate Email"
              type="email"
              placeholder="colleague@company.com"
              value={quickShareEmail}
              onChange={(e) => setQuickShareEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleShareExistingSubmit();
                }
              }}
              fullWidth
              size="small"
              disabled={isQuickSharing}
            />
            <Button
              variant="contained"
              onClick={handleShareExistingSubmit}
              disabled={!quickShareEmail.trim() || !quickShareEmail.includes('@') || isQuickSharing}
              startIcon={isQuickSharing ? <CircularProgress size={14} color="inherit" /> : <PersonAdd sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: 'var(--hp-primary)',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                flexShrink: 0
              }}
            >
              {isQuickSharing ? 'Sharing...' : 'Share'}
            </Button>
          </Box>

          {/* Currently shared participants list */}
          {sharingTemplate && ((sharingTemplate.shared_users && sharingTemplate.shared_users.length > 0) || (sharedUsersMap[sharingTemplate.brd_id] || []).length > 0) && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--hp-text-secondary)', display: 'block', mb: 0.8 }}>
                Currently Shared With:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                {(sharingTemplate.shared_users || sharedUsersMap[sharingTemplate.brd_id] || []).map((email) => (
                  <Chip
                    key={email}
                    avatar={<Avatar sx={{ bgcolor: 'var(--hp-primary)', color: '#fff' }}>{email.charAt(0).toUpperCase()}</Avatar>}
                    label={email}
                    size="small"
                    sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', fontWeight: 600 }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setSharingTemplate(null)} sx={{ textTransform: 'none' }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================== */}
      {/* DELETE CONFIRMATION DIALOG */}
      {/* ======================================================== */}
      <Dialog
        open={Boolean(deletingTemplate)}
        onClose={() => !isDeleting && setDeletingTemplate(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: '18px', p: 1, bgcolor: 'var(--hp-surface)' }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteOutline sx={{ color: '#FF6577' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--hp-text)' }}>
              Delete BRD Template?
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => !isDeleting && setDeletingTemplate(null)} disabled={isDeleting}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 1.5 }}>
          <DialogContentText sx={{ color: 'var(--hp-text-secondary)', fontSize: '0.88rem' }}>
            Are you sure you want to delete <strong>{deletingTemplate?.brd_template_name}</strong>? This template will be permanently removed from BigQuery and your workspace.
          </DialogContentText>

          {deleteError && (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: '8px', fontSize: '0.82rem' }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => setDeletingTemplate(null)}
            disabled={isDeleting}
            sx={{ textTransform: 'none', color: 'var(--hp-text-secondary)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
            sx={{
              bgcolor: '#FF6577',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#e04d60' }
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================== */}
      {/* VIEW README TEMPLATE DIALOG */}
      {/* ======================================================== */}
      <Dialog
        open={Boolean(viewingTemplate)}
        onClose={() => setViewingTemplate(null)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: '20px', p: 1, bgcolor: 'var(--hp-surface)' }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description sx={{ color: 'var(--hp-primary)' }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {viewingTemplate?.brd_template_name}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setViewingTemplate(null)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  size="small"
                  label={
                    viewingTemplate && isTemplateOwner(viewingTemplate, user?.email)
                      ? 'Owner'
                      : 'Shared with you'
                  }
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    bgcolor:
                      viewingTemplate && isTemplateOwner(viewingTemplate, user?.email)
                        ? 'rgba(34, 201, 154, 0.14)'
                        : 'rgba(79, 107, 255, 0.14)',
                    color:
                      viewingTemplate && isTemplateOwner(viewingTemplate, user?.email)
                        ? '#1ca87f'
                        : 'var(--hp-primary)'
                  }}
                />
                <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', fontWeight: 500 }}>
                  Owner: {viewingTemplate?.brd_owner_email} • Created:{' '}
                  {formatTemplateDate(viewingTemplate?.created_date)}
                </Typography>
              </Box>

              {/* Shared users in view dialog */}
              {viewingTemplate?.shared_users && viewingTemplate.shared_users.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.8 }}>
                  <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ fontSize: '0.72rem' }}>
                    Shared with:
                  </Typography>
                  <AvatarGroup
                    max={5}
                    sx={{
                      '& .MuiAvatar-root': {
                        width: 22,
                        height: 22,
                        fontSize: '0.65rem',
                        bgcolor: 'var(--hp-primary)'
                      }
                    }}
                  >
                    {viewingTemplate.shared_users.map((email, idx) => (
                      <Tooltip key={idx} title={email}>
                        <Avatar
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(formatNameFromEmail(email))}&background=4F6BFF&color=fff`}
                        >
                          {email.charAt(0).toUpperCase()}
                        </Avatar>
                      </Tooltip>
                    ))}
                  </AvatarGroup>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {viewingTemplate && isTemplateOwner(viewingTemplate, user?.email) && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Share sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    if (viewingTemplate) {
                      handleOpenShare(viewingTemplate);
                    }
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.78rem', borderRadius: '8px' }}
                >
                  Share
                </Button>
              )}

              <Button
                size="small"
                startIcon={<ContentCopy sx={{ fontSize: 14 }} />}
                onClick={handleCopyReadme}
                sx={{ textTransform: 'none', fontSize: '0.78rem' }}
              >
                {copied ? 'Copied Markdown!' : 'Copy Markdown'}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: 'var(--hp-bg)',
              borderRadius: '12px',
              border: '1px solid var(--hp-border)',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color: 'var(--hp-text)',
              maxHeight: 450,
              overflowY: 'auto'
            }}
          >
            <pre
              style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit'
              }}
            >
              {viewingTemplate?.brd_template_readme || 'No description provided.'}
            </pre>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          {/* Owner-only Actions in View Dialog */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {viewingTemplate && isTemplateOwner(viewingTemplate, user?.email) && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const tmpl = viewingTemplate;
                    handleOpenDelete(tmpl);
                  }}
                  startIcon={<DeleteOutline />}
                  sx={{
                    color: '#FF6577',
                    borderColor: 'rgba(255, 101, 119, 0.4)',
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#FF6577',
                      bgcolor: 'rgba(255, 101, 119, 0.08)'
                    }
                  }}
                >
                  Delete
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => {
                    const tmpl = viewingTemplate;
                    handleOpenEdit(tmpl);
                  }}
                  startIcon={<Edit />}
                  sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
                >
                  Edit
                </Button>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setViewingTemplate(null)} sx={{ textTransform: 'none' }}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleCopyReadme}
              startIcon={<ContentCopy />}
              sx={{
                bgcolor: 'var(--hp-primary)',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {copied ? 'Copied to Clipboard' : 'Copy Template Markdown'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={3500}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastMessage(null)} severity="success" sx={{ borderRadius: '12px' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplatesPage;
